'use client'

import * as Sentry from '@sentry/nextjs'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

import kratos from '@/utils/ory-client'

export type KratosUser = {
  id: string
  traits: {
    email: string
    name?: {
      first: string
      last: string
    }
    picture?: string
  }
}

interface AuthContextType {
  user: KratosUser | null
  isLoading: boolean
  error: string | null
  /** Re-fetch session (e.g. after login/signup). Returns the current user or null. */
  refreshSession: () => Promise<KratosUser | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<KratosUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Keep Sentry user in sync with the active session.
  useEffect(() => {
    if (!user) {
      Sentry.setUser(null)
      return
    }
    const email = String(user?.traits?.email || '').trim()
    const first = String(user?.traits?.name?.first || '').trim()
    const last = String(user?.traits?.name?.last || '').trim()
    const username = [first, last].filter(Boolean).join(' ').trim() || email
    Sentry.setUser({
      id: String(user.id || ''),
      email: email || undefined,
      username: username || undefined,
    })
  }, [user])

  const refreshSession = useCallback(async (): Promise<KratosUser | null> => {
    try {
      setIsLoading(true)
      setError(null)
      const { data: session } = await kratos.toSession(undefined, {
        withCredentials: true,
        headers: { Accept: 'application/json' }
      })
      const identity = session.identity || null
      setUser(identity)
      return identity
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        setUser(null)
        setError(null)
        return null
      }
      console.error('Failed to get session:', error)
      setUser(null)
      setError(error instanceof Error ? error.message : 'Failed to get session')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshSession()
  }, [refreshSession])

  return (
    <AuthContext.Provider value={{ user, isLoading, error, refreshSession }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Backward compatibility - keep the old hook but make it use the context
export function useAuthUser() {
  const { user } = useAuth()
  return user
}
